// Agreements — surfaces Agreement-agent-extracted redlines for inbound
// contracts (NDAs, MSAs, SOWs). Two-column layout mirrors Inbox /
// Meetings / Focus polish standard.
//
// Read shape:
//   - Left column: agreements grouped by status (under_review / reviewed
//     / signed / cancelled). draft status (created without a redline)
//     is hidden — the agent only inserts rows that have a redline ready.
//   - Right column: the selected agreement — redline_summary (monospace
//     block, owner-facing) + concerns expanded by severity + status
//     transition buttons.
//
// Mutation shape:
//   - Status transitions: UPDATE agreements SET status='reviewed' /
//     'signed' / 'cancelled'. No outbound API anywhere — we never
//     sign on behalf of the owner; "signed" here is the owner's
//     audit-trail mark after they signed in a real signing tool
//     (DocuSign, paper, etc.).
//   - reviewed_at is set automatically when status transitions OUT of
//     under_review.
//
// N1 reminder: this page does NOT auto-reply to the sender, does NOT
// forward the redline, does NOT email signed status anywhere. The agent
// produces the redline; the owner reviews it.

import { useEffect, useMemo, useState, type ComponentType, type SVGProps } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  FileText,
  GitPullRequestArrow,
  ShieldCheck,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import GlassCard from '@/components/shared/GlassCard';
import PageStatsRow from '@/components/shared/PageStatsRow';
import PulseDot from '@/components/shared/PulseDot';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

type RedlineSeverity = 'low' | 'medium' | 'high';

type RedlineConcern = {
  section: string;
  concern: string;
  severity: RedlineSeverity;
};

type AgreementStatus = 'draft' | 'under_review' | 'reviewed' | 'signed' | 'cancelled';

type AgreementRow = {
  id: string;
  title: string;
  source_type: 'email' | 'meeting' | 'manual' | 'other';
  source_email_id: string | null;
  document_url: string | null;
  redline_summary: string | null;
  redline_concerns: RedlineConcern[];
  redline_generated_at: string | null;
  status: AgreementStatus;
  reviewed_at: string | null;
  counterparty_name: string | null;
  counterparty_email: string | null;
  external_metadata: {
    attachment_filename?: string;
    attachment_mime?: string;
    pdf_pages?: number;
    pdf_truncated?: boolean;
  };
  created_at: string;
};

const AGREEMENT_SELECT =
  'id, title, source_type, source_email_id, document_url, redline_summary, redline_concerns, redline_generated_at, status, reviewed_at, counterparty_name, counterparty_email, external_metadata, created_at';

async function fetchActive(): Promise<AgreementRow[]> {
  const { data, error } = await supabase
    .from('agreements')
    .select(AGREEMENT_SELECT)
    .is('deleted_at', null)
    .in('status', ['under_review', 'reviewed'])
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data as unknown as AgreementRow[]) ?? [];
}

async function fetchArchived(): Promise<AgreementRow[]> {
  const { data, error } = await supabase
    .from('agreements')
    .select(AGREEMENT_SELECT)
    .is('deleted_at', null)
    .in('status', ['signed', 'cancelled'])
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) throw error;
  return (data as unknown as AgreementRow[]) ?? [];
}

// Lightweight aggregate fetch for the summary + analytics sections — pulls
// every non-deleted, non-draft agreement (the list queries are status-filtered
// + capped, so they can't drive accurate totals). Drafts are excluded to match
// the visible list, which hides them (the agent only surfaces redlined rows),
// so the tile total reconciles with what Joel sees below. Minimal columns;
// client-side rollup.
type AgreementStatRow = {
  status: AgreementStatus;
  redline_summary: string | null;
  redline_concerns: RedlineConcern[] | null;
};

async function fetchAgreementStats(): Promise<AgreementStatRow[]> {
  const { data, error } = await supabase
    .from('agreements')
    .select('status, redline_summary, redline_concerns')
    .is('deleted_at', null)
    .neq('status', 'draft')
    .limit(1000);
  if (error) throw error;
  return (data as unknown as AgreementStatRow[]) ?? [];
}

type Analytics = {
  total: number;
  byStatus: Record<AgreementStatus, number>;
  underReview: number;
  redlined: number;
  redlineCoveragePct: number;
  concerns: { high: number; medium: number; low: number; total: number };
};

function computeAnalytics(rows: AgreementStatRow[]): Analytics {
  const byStatus: Record<AgreementStatus, number> = {
    draft: 0,
    under_review: 0,
    reviewed: 0,
    signed: 0,
    cancelled: 0,
  };
  const concerns = { high: 0, medium: 0, low: 0, total: 0 };
  let redlined = 0;
  for (const r of rows) {
    byStatus[r.status] = (byStatus[r.status] ?? 0) + 1;
    if (r.redline_summary) redlined += 1;
    for (const c of r.redline_concerns ?? []) {
      concerns.total += 1;
      if (c.severity === 'high') concerns.high += 1;
      else if (c.severity === 'medium') concerns.medium += 1;
      else concerns.low += 1;
    }
  }
  const total = rows.length;
  return {
    total,
    byStatus,
    underReview: byStatus.under_review,
    redlined,
    redlineCoveragePct: total === 0 ? 0 : Math.round((redlined / total) * 100),
    concerns,
  };
}

function severityTone(s: RedlineSeverity): {
  bg: string;
  text: string;
  ring: string;
  label: string;
} {
  switch (s) {
    case 'high':
      return { bg: 'bg-alert/10', text: 'text-alert', ring: 'ring-alert/30', label: 'High' };
    case 'medium':
      return {
        bg: 'bg-attention/10',
        text: 'text-attention',
        ring: 'ring-attention/30',
        label: 'Medium',
      };
    case 'low':
    default:
      return {
        bg: 'bg-secondary',
        text: 'text-muted-foreground',
        ring: 'ring-border',
        label: 'Low',
      };
  }
}

function statusTone(s: AgreementStatus): { bg: string; text: string; label: string } {
  switch (s) {
    case 'under_review':
      return { bg: 'bg-forest-soft', text: 'text-forest', label: 'Under review' };
    case 'reviewed':
      return { bg: 'bg-sage/10', text: 'text-sage', label: 'Reviewed' };
    case 'signed':
      return { bg: 'bg-learning/10', text: 'text-learning', label: 'Signed' };
    case 'cancelled':
      return { bg: 'bg-alert/10', text: 'text-alert', label: 'Cancelled' };
    case 'draft':
    default:
      return { bg: 'bg-secondary', text: 'text-muted-foreground', label: 'Draft' };
  }
}

// Visualizer palette — design-token hexes so the charts harmonize with the
// rest of the UI without depending on Tailwind theme resolution inside SVG.
const VIZ = {
  forest: '#2D7D5F',
  sage: '#7BA890',
  blue: '#3B6E8F',
  alert: '#B94A3C',
  attention: '#C8941F',
  muted: '#9AA39C',
} as const;

type Segment = { label: string; value: number; color: string };

// Proportional stacked bar + legend. Pure CSS — no chart lib.
function SegmentBar({
  title,
  icon: Icon,
  segments,
  emptyHint,
}: {
  title: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  segments: Segment[];
  emptyHint: string;
}) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  return (
    <div>
      <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        <Icon className="w-3.5 h-3.5" aria-hidden />
        {title}
      </p>
      <div
        className="mt-2 flex h-2.5 w-full overflow-hidden rounded-full bg-secondary neo-inset"
        role="img"
        aria-label={`${title}: ${segments.map((s) => `${s.value} ${s.label}`).join(', ')}`}
      >
        {total === 0
          ? null
          : segments
              .filter((s) => s.value > 0)
              .map((s) => (
                <div
                  key={s.label}
                  className="h-full"
                  style={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }}
                />
              ))}
      </div>
      {total === 0 ? (
        <p className="mt-2 text-[11px] text-muted-foreground/80">{emptyHint}</p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {segments
            .filter((s) => s.value > 0)
            .map((s) => (
              <li key={s.label} className="flex items-center gap-2 text-xs">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: s.color }}
                  aria-hidden
                />
                <span className="text-muted-foreground">{s.label}</span>
                <span className="ml-auto font-mono text-foreground">{s.value}</span>
                <span className="font-mono text-muted-foreground/70 w-9 text-right">
                  {Math.round((s.value / total) * 100)}%
                </span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

// SVG ring for redline coverage. Track is a low-opacity muted stroke so it
// reads in both light + dark without theme plumbing.
function CoverageDonut({
  percent,
  centerLabel,
  caption,
}: {
  percent: number;
  centerLabel: string;
  caption: string;
}) {
  const r = 40;
  const circumference = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, percent)) / 100) * circumference;
  return (
    <div className="flex flex-col items-center justify-center text-center">
      <div className="relative w-28 h-28">
        <svg viewBox="0 0 96 96" className="w-28 h-28 -rotate-90" aria-hidden>
          <circle
            cx="48"
            cy="48"
            r={r}
            fill="none"
            strokeWidth="9"
            stroke={VIZ.muted}
            strokeOpacity={0.18}
          />
          {percent > 0 && (
            // Only render the arc when there's progress — a round-capped
            // zero-length dash would otherwise show a dot at 3 o'clock.
            <circle
              cx="48"
              cy="48"
              r={r}
              fill="none"
              strokeWidth="9"
              strokeLinecap="round"
              stroke={VIZ.forest}
              strokeDasharray={`${dash} ${circumference}`}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-mono font-semibold text-foreground">{centerLabel}</span>
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground max-w-[12rem]">{caption}</p>
    </div>
  );
}

// Section 2 — the analytics card. Three small visualizers in a responsive grid.
function AgreementAnalytics({ a }: { a: Analytics }) {
  const pipeline: Segment[] = [
    { label: 'Under review', value: a.byStatus.under_review, color: VIZ.forest },
    { label: 'Reviewed', value: a.byStatus.reviewed, color: VIZ.sage },
    { label: 'Signed', value: a.byStatus.signed, color: VIZ.blue },
    { label: 'Cancelled', value: a.byStatus.cancelled, color: VIZ.alert },
  ];
  const risk: Segment[] = [
    { label: 'High', value: a.concerns.high, color: VIZ.alert },
    { label: 'Medium', value: a.concerns.medium, color: VIZ.attention },
    { label: 'Low', value: a.concerns.low, color: VIZ.muted },
  ];
  return (
    <GlassCard hover={false}>
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-sm font-medium text-foreground">Analytics</h2>
        <span className="text-xs text-muted-foreground">
          — pipeline, risk profile, and coverage
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        <SegmentBar
          title="Pipeline"
          icon={GitPullRequestArrow}
          segments={pipeline}
          emptyHint="No agreements in the pipeline yet."
        />
        <SegmentBar
          title="Risk profile"
          icon={AlertTriangle}
          segments={risk}
          emptyHint="No clauses flagged — clean so far."
        />
        <div className="flex items-center justify-center">
          <CoverageDonut
            percent={a.redlineCoveragePct}
            centerLabel={`${a.redlineCoveragePct}%`}
            caption={`${a.redlined} of ${a.total} agreements redlined by the agent`}
          />
        </div>
      </div>
    </GlassCard>
  );
}

const Agreements = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const activeQ = useQuery({ queryKey: ['agreements', 'active'], queryFn: fetchActive });
  const archivedQ = useQuery({ queryKey: ['agreements', 'archived'], queryFn: fetchArchived });
  const statsQ = useQuery({ queryKey: ['agreements', 'stats'], queryFn: fetchAgreementStats });

  const allActive = activeQ.data ?? [];
  const allArchived = archivedQ.data ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Keep selection live: prefer active rows, fall back to archived.
  useEffect(() => {
    if (!activeQ.data || !archivedQ.data) return;
    const ids = [...allActive.map((r) => r.id), ...allArchived.map((r) => r.id)];
    setSelectedId((curr) => (curr && ids.includes(curr) ? curr : (ids[0] ?? null)));
  }, [activeQ.data, archivedQ.data, allActive, allArchived]);

  const selected = [...allActive, ...allArchived].find((r) => r.id === selectedId) ?? null;

  const setStatus = useMutation({
    mutationFn: async (args: { id: string; status: Exclude<AgreementStatus, 'draft'> }) => {
      if (!user) throw new Error('No user');
      const patch: Record<string, unknown> = { status: args.status };
      // reviewed_at is the owner's "I have read this" mark — set the
      // first time the row leaves under_review and stays set thereafter.
      if (args.status !== 'under_review') {
        patch.reviewed_at = new Date().toISOString();
      }
      const { error } = await supabase.from('agreements').update(patch).eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agreements', 'active'] });
      qc.invalidateQueries({ queryKey: ['agreements', 'archived'] });
      // Keep the summary tiles + pipeline in sync with the list after a status
      // change — otherwise they show pre-transition counts until the next poll.
      qc.invalidateQueries({ queryKey: ['agreements', 'stats'] });
    },
  });

  const rowPending = (id: string): boolean => setStatus.isPending && setStatus.variables?.id === id;

  const underReviewCount = allActive.filter((r) => r.status === 'under_review').length;
  const totalCount = allActive.length + allArchived.length;
  const highConcernCount = allActive.reduce(
    (n, r) => n + (r.redline_concerns ?? []).filter((c) => c.severity === 'high').length,
    0,
  );

  // Aggregate across ALL agreements (the list queries are filtered + capped).
  const analytics = useMemo(
    () => (statsQ.data ? computeAnalytics(statsQ.data) : null),
    [statsQ.data],
  );

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex items-start justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-serif font-semibold tracking-tight text-foreground">
            Agreements
          </h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            {totalCount === 0
              ? 'No agreements yet. The agent extracts redlines from inbound PDFs (NDAs / MSAs / SOWs) within ~60s of arrival.'
              : `${underReviewCount} under review${highConcernCount > 0 ? ` · ${highConcernCount} high-severity concern${highConcernCount === 1 ? '' : 's'} flagged` : ''}. The agent surfaces redlines; you decide.`}
          </p>
        </div>
      </motion.header>

      {/* Section 1 — quick summary. Rendered during load too (em-dash values)
          so the tiles reserve their space instead of popping in over the list. */}
      {(statsQ.isLoading || (analytics !== null && analytics.total > 0)) && (
        <PageStatsRow
          stats={[
            {
              label: 'Agreements',
              value: analytics?.total,
              icon: FileText,
              secondary: 'tracked',
            },
            {
              label: 'Under review',
              value: analytics?.underReview,
              icon: ClipboardCheck,
              iconClassName:
                (analytics?.underReview ?? 0) > 0 ? 'text-forest' : 'text-muted-foreground',
              valueClassName: (analytics?.underReview ?? 0) > 0 ? 'text-forest' : 'text-foreground',
              secondary: 'awaiting you',
            },
            {
              label: 'High-severity',
              value: analytics?.concerns.high,
              icon: AlertTriangle,
              iconClassName:
                (analytics?.concerns.high ?? 0) > 0 ? 'text-alert' : 'text-muted-foreground',
              valueClassName:
                (analytics?.concerns.high ?? 0) > 0 ? 'text-alert' : 'text-foreground',
              secondary: 'clauses flagged',
            },
            {
              label: 'Redlined',
              value: analytics?.redlineCoveragePct,
              suffix: '%',
              icon: Sparkles,
              iconClassName: 'text-sage',
              valueClassName: 'text-sage',
              secondary: analytics
                ? `${analytics.redlined}/${analytics.total} by the agent`
                : 'by the agent',
            },
          ]}
        />
      )}

      {/* Section 2 — analytics visualizers (only once we have data) */}
      {analytics && analytics.total > 0 && <AgreementAnalytics a={analytics} />}

      <section>
        {(activeQ.isLoading || archivedQ.isLoading) && (
          <p className="text-sm text-muted-foreground">Loading agreements…</p>
        )}
        {(activeQ.isError || archivedQ.isError) && (
          <GlassCard hover={false} className="neo-alert">
            <p className="text-sm text-alert">
              Failed to load agreements:{' '}
              {((activeQ.error ?? archivedQ.error) as Error | undefined)?.message}
            </p>
          </GlassCard>
        )}

        {totalCount === 0 && !activeQ.isLoading && !archivedQ.isLoading && (
          <GlassCard hover={false} variant="inset">
            <p className="text-sm text-muted-foreground">
              No agreements yet. Inbound PDFs whose subject mentions NDA / contract / MSA / SOW are
              picked up and redlined here.
            </p>
            <p className="mt-2 text-[11px] text-muted-foreground/80">
              <ShieldCheck className="w-3 h-3 inline mr-1" />
              The agent never signs on your behalf and never replies to the counterparty (N1).
            </p>
          </GlassCard>
        )}

        {totalCount > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 lg:h-[calc(100vh-14rem)] lg:min-h-[560px]">
            {/* List column */}
            <div className="lg:col-span-2 space-y-3 lg:overflow-y-auto lg:pr-2">
              {allActive.length > 0 && (
                <>
                  <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground/80 mt-1">
                    Active · {allActive.length}
                  </h2>
                  <div className="space-y-3">
                    <AnimatePresence mode="popLayout" initial={false}>
                      {allActive.map((r) => (
                        <motion.div
                          key={r.id}
                          layout
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.25 }}
                        >
                          <AgreementListItem
                            row={r}
                            selected={r.id === selectedId}
                            onSelect={() => setSelectedId(r.id)}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </>
              )}
              {allArchived.length > 0 && (
                <>
                  <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground/80 mt-3">
                    Archive · {allArchived.length}
                  </h2>
                  <div className="space-y-3">
                    {allArchived.map((r) => (
                      <AgreementListItem
                        key={r.id}
                        row={r}
                        selected={r.id === selectedId}
                        onSelect={() => setSelectedId(r.id)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Detail column */}
            <div className="lg:col-span-3 lg:h-full lg:overflow-y-auto">
              {selected ? (
                <AgreementDetail
                  row={selected}
                  rowPending={rowPending(selected.id)}
                  onStatusChange={(s) => setStatus.mutate({ id: selected.id, status: s })}
                />
              ) : (
                <GlassCard hover={false} variant="inset" className="h-full min-h-[400px]">
                  <p className="text-sm text-muted-foreground">Select an agreement to view.</p>
                </GlassCard>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

type AgreementListItemProps = {
  row: AgreementRow;
  selected: boolean;
  onSelect: () => void;
};

function AgreementListItem({ row, selected, onSelect }: AgreementListItemProps) {
  const tone = statusTone(row.status);
  const concerns = row.redline_concerns ?? [];
  const highCount = concerns.filter((c) => c.severity === 'high').length;
  const mediumCount = concerns.filter((c) => c.severity === 'medium').length;
  return (
    <GlassCard
      hover={false}
      ariaPressed={selected}
      className={`cursor-pointer transition-all relative ${
        selected
          ? 'neo-selected before:absolute before:left-1 before:top-3 before:bottom-3 before:w-1 before:rounded-r before:bg-forest'
          : ''
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2 mb-2">
        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium text-foreground truncate">{row.title}</span>
        <span
          className={`ml-auto text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip ${tone.bg} ${tone.text} shrink-0`}
        >
          {tone.label}
        </span>
      </div>
      <div className="flex items-center gap-3 text-[11px] font-mono text-muted-foreground">
        {row.counterparty_name && (
          <span className="truncate max-w-[160px]">{row.counterparty_name}</span>
        )}
        <span>{formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}</span>
      </div>
      {(highCount > 0 || mediumCount > 0) && (
        <div className="mt-2 flex gap-2 flex-wrap">
          {highCount > 0 && (
            <Badge variant="secondary" className="neo-chip bg-alert/10 text-alert">
              {highCount} high
            </Badge>
          )}
          {mediumCount > 0 && (
            <Badge variant="secondary" className="neo-chip bg-attention/10 text-attention">
              {mediumCount} medium
            </Badge>
          )}
        </div>
      )}
      {!row.redline_summary && (
        <p className="mt-2 text-[11px] text-muted-foreground/80 flex items-center gap-1">
          <PulseDot color="amber" /> awaiting redline
        </p>
      )}
    </GlassCard>
  );
}

type AgreementDetailProps = {
  row: AgreementRow;
  rowPending: boolean;
  onStatusChange: (s: Exclude<AgreementStatus, 'draft'>) => void;
};

function AgreementDetail({ row, rowPending, onStatusChange }: AgreementDetailProps) {
  const tone = statusTone(row.status);
  const concerns = row.redline_concerns ?? [];
  const byHigh = concerns.filter((c) => c.severity === 'high');
  const byMedium = concerns.filter((c) => c.severity === 'medium');
  const byLow = concerns.filter((c) => c.severity === 'low');
  return (
    <GlassCard hover={false} className="flex flex-col">
      <div className="pb-4 border-b border-border">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span
            className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip ${tone.bg} ${tone.text}`}
          >
            {tone.label}
          </span>
          {row.external_metadata?.pdf_pages !== undefined && (
            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip bg-secondary text-muted-foreground">
              {row.external_metadata.pdf_pages} page
              {row.external_metadata.pdf_pages === 1 ? '' : 's'}
              {row.external_metadata.pdf_truncated ? ' · trimmed' : ''}
            </span>
          )}
          {row.document_url && (
            <a
              href={row.document_url}
              target="_blank"
              rel="noreferrer"
              className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip bg-learning/10 text-learning hover:opacity-80 transition-opacity flex items-center gap-1"
            >
              open email <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <span className="ml-auto text-[11px] font-mono text-muted-foreground">
            {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
          </span>
        </div>
        <h3 className="text-lg font-medium text-foreground">{row.title}</h3>
        {(row.counterparty_name || row.counterparty_email) && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Counterparty:{' '}
            <span className="text-foreground">
              {row.counterparty_name ?? row.counterparty_email}
            </span>
            {row.counterparty_name && row.counterparty_email && (
              <span className="text-muted-foreground"> &lt;{row.counterparty_email}&gt;</span>
            )}
          </p>
        )}
      </div>

      {/* Redline summary */}
      {row.redline_summary ? (
        <div className="py-4 border-b border-border">
          <p className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-forest">
            <Sparkles className="w-3 h-3" /> Redline summary
            {row.redline_generated_at && (
              <span className="text-muted-foreground/80 ml-1 normal-case tracking-normal">
                · generated{' '}
                {formatDistanceToNow(new Date(row.redline_generated_at), { addSuffix: true })}
              </span>
            )}
          </p>
          {/* Recessed neo-inset well (matches Inbox + Meetings detail panes). */}
          <pre className="whitespace-pre-wrap break-words neo-inset p-3 mt-2 font-sans text-sm text-foreground leading-relaxed">
            {row.redline_summary}
          </pre>
        </div>
      ) : (
        <div className="py-4 border-b border-border">
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <PulseDot color="amber" /> The agent is preparing a redline for this agreement.
          </p>
        </div>
      )}

      {/* Concerns */}
      {concerns.length > 0 && (
        <div className="py-4 border-b border-border">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
            Clauses to look at twice · {concerns.length}
          </p>
          <ConcernGroup label="High severity" tone="high" items={byHigh} />
          <ConcernGroup label="Medium severity" tone="medium" items={byMedium} />
          <ConcernGroup label="Low severity" tone="low" items={byLow} />
        </div>
      )}
      {row.redline_summary && concerns.length === 0 && (
        <div className="py-4 border-b border-border">
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-sage" />
            The agent didn't flag anything material. Still your call.
          </p>
        </div>
      )}

      {/* Status actions */}
      <div className="py-4 flex flex-col gap-2">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Owner action</p>
        <div className="flex gap-2 flex-wrap">
          {row.status === 'under_review' && (
            <Button
              size="sm"
              onClick={() => onStatusChange('reviewed')}
              disabled={rowPending}
              aria-label="Mark reviewed"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" /> Mark reviewed
            </Button>
          )}
          {(row.status === 'under_review' || row.status === 'reviewed') && (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onStatusChange('signed')}
                disabled={rowPending}
                aria-label="Mark signed"
              >
                <ShieldCheck className="w-4 h-4 mr-1" /> Mark signed
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onStatusChange('cancelled')}
                disabled={rowPending}
                aria-label="Cancel"
                className="text-alert hover:bg-alert/10"
              >
                <XCircle className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </>
          )}
          {(row.status === 'signed' || row.status === 'cancelled') && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onStatusChange('under_review')}
              disabled={rowPending}
              aria-label="Reopen"
            >
              Reopen
            </Button>
          )}
        </div>
      </div>

      <p className="mt-auto pt-3 text-[11px] text-muted-foreground/80">
        <ShieldCheck className="w-3 h-3 inline mr-1" />
        Redline is owner-only — never sent to the counterparty. "Signed" is your audit mark; we
        never sign on your behalf (N1).
      </p>
    </GlassCard>
  );
}

function ConcernGroup({
  label,
  tone,
  items,
}: {
  label: string;
  tone: RedlineSeverity;
  items: RedlineConcern[];
}) {
  if (items.length === 0) return null;
  const t = severityTone(tone);
  return (
    <div className="mt-3 first:mt-0">
      <p className={`flex items-center gap-1 text-[10px] uppercase tracking-wide ${t.text}`}>
        {tone === 'high' && <AlertTriangle className="w-3 h-3" />}
        {label} · {items.length}
      </p>
      <ul className="space-y-2 mt-1">
        {items.map((c, i) => (
          <li
            key={`${c.section}-${i}`}
            className={`rounded-md neo-inset ${t.bg} px-3 py-2 text-sm`}
          >
            <p className={`text-[11px] uppercase tracking-wide ${t.text}`}>{c.section}</p>
            <p className="text-sm text-foreground mt-1 leading-snug">{c.concern}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default Agreements;
